import React, { useEffect, useState } from 'react';
import { BsChevronDown, BsXLg } from 'react-icons/bs';
import {
  Box,
  Button,
  Flex, HStack,
  Icon, IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text, Tooltip,
  useDisclosure
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { analyticsIds } from '../../constants/analyticsIds';
import { CypressIds } from '../../constants/cypressIds';
import { RequirementTemplatesDropdownDuskIds } from '../../constants/duskIds';
import colors from '../../styles/theme/foundation/colors';
import { getButtonVariantStyles } from '../../styles/theme/util/dropdownStylesUtil';
import { activeStyle, hoverStyle } from '../../styles/theme/util/popoverMenuButtonStyles';

import Content from './Content';

const RequirementTemplateDropdownMenu = ({
  companyId,
  defaultTemplate,
  variant = 'secondary',
  onChange = () => {},
  showResetButton = true,
  onUploadContract = null,
  dropdownId = null
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentTemplate, setCurrentTemplate] = useState(null);

  useEffect(() => {
    setCurrentTemplate(defaultTemplate);
  }, [defaultTemplate]);

  const onSelect = value => {
    setCurrentTemplate(value);

    if (value?.id !== currentTemplate?.id) {
      onChange(value);
    }

    onClose();
  };

  const onReset = () => {
    setCurrentTemplate(null);
    onChange(null);
    onClose();
  };

  return (
    <Box zIndex={5}>
      <HStack id={dropdownId}>
        <Popover
          closeOnBlur
          isOpen={isOpen}
          onClose={onClose}
          onOpen={onOpen}
        >
          <PopoverTrigger>
            <Button
              _active={activeStyle}
              _hover={hoverStyle}
              data-analytics-id={analyticsIds.templatesPage.templatesDropdown}
              data-cy={CypressIds.reqTemplateMenuButton}
              dusk={RequirementTemplatesDropdownDuskIds.menuButton}
              style={getButtonVariantStyles(variant, isOpen, currentTemplate?.id)}
              variant="unstyled"
              width="100%"
            >
              <Flex alignItems="center" justify="space-between" pointerEvents="none" width="100%">
                <Text>{currentTemplate?.name || 'Select Template'}</Text>
                <Icon as={BsChevronDown} color={colors.primaryScale['600']} />
              </Flex>
            </Button>
          </PopoverTrigger>
          <PopoverContent maxH={300} overflow="hidden auto" width="400px">
            <Content
              companyId={companyId}
              onChange={onSelect}
              onUploadContract={onUploadContract && (() => {
                onClose();
                onUploadContract();
              })}
            />
          </PopoverContent>
        </Popover>
        {showResetButton && (
          <Tooltip label="Reset default requirement template" placement="top">
            <IconButton
              aria-label="remove-template"
              data-cy={CypressIds.reqTemplateResetButton}
              icon={<Icon as={BsXLg} />}
              size="xs"
              variant="textGrey"
              width="10%"
              onClick={onReset}
            />
          </Tooltip>
        )}
      </HStack>
      {onUploadContract && (
        <Text bgClip="text" bgGradient={colors.gradient.aiPrimary} mt={1} textStyle="bodyXs">
          Upload contract available in dropdown
        </Text>
      )}
    </Box>
  );
};

RequirementTemplateDropdownMenu.propTypes = {
  /** Variant for the trigger button */
  variant: PropTypes.oneOf(['primary', 'secondary']),
  defaultTemplate: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  }),
  /** Change event when template is selected */
  onChange: PropTypes.func,
  companyId: PropTypes.number.isRequired,
  showResetButton: PropTypes.bool,
  /** When provided, renders an "Upload Contract" button inside the dropdown */
  onUploadContract: PropTypes.func,
  /** ID for the dropdown */
  dropdownId: PropTypes.string
};

export default RequirementTemplateDropdownMenu;
